import React, { useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export interface ModalProps {
  /** Modal heading (rendered in the header and referenced by aria-labelledby). */
  title: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  /** Footer content (e.g. Cancel / primary action buttons). */
  footer?: React.ReactNode;
  /** Bootstrap modal-dialog size modifier. */
  size?: 'sm' | 'lg' | 'xl';
  /** Accessible label for the header close (×) button. */
  closeAriaLabel?: string;
  /** Stacking order — raise for a modal shown on top of another (default 1050). */
  zIndex?: number;
  /** Fixed content between the header and the scrolling body (e.g. a stepper). */
  subheader?: React.ReactNode;
  /** Render full screen below this breakpoint (Bootstrap `modal-fullscreen-*-down`). */
  fullscreenBelow?: 'sm' | 'md' | 'lg';
  /** Extra class on `.modal-dialog`, for component-specific sizing. */
  dialogClassName?: string;
  /**
   * Custom header content in place of the plain title, e.g. the objection
   * design's kicker, title and status pill. The element naming the dialog must
   * carry the given id; the close button is still rendered.
   */
  renderHeader?: (titleId: string) => React.ReactNode;
  /** Extra class on `.modal-header`, e.g. `objection-header`. */
  headerClassName?: string;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Body scroll lock is ref-counted so that stacked modals cooperate: the first
// modal to open locks the body, and only the last one to close restores the
// original overflow. Without this, a nested modal captures the already-locked
// value and re-applies it on unmount, leaving the page scroll-locked.
let openModalCount = 0;
let savedBodyOverflow = '';

/**
 * Accessible modal following the app's Bootstrap `.modal.d-block` pattern, with
 * the behaviours the house modals lacked: Escape to close, click-outside to
 * close, focus moved in on open and trapped while open, focus restored to the
 * opener on close, and a body scroll lock so the background never scrolls.
 * Rendered inline (no portal) so it stays within its parent's DOM subtree.
 */
export default function Modal({
  title,
  onClose,
  children,
  footer,
  size,
  closeAriaLabel,
  zIndex = 1050,
  subheader,
  fullscreenBelow,
  dialogClassName,
  renderHeader,
  headerClassName,
}: ModalProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  // Always call the latest onClose from the (mount-only) keydown handler without
  // re-running the setup effect. Parents often pass a fresh onClose on every
  // render; if the effect depended on it, each keystroke would re-run setup and
  // yank focus back to the first field, making inputs impossible to type into.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null;

    // Lock background scroll while the modal is open (ref-counted for stacking).
    if (openModalCount === 0) {
      savedBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    openModalCount += 1;

    // Move focus into the modal: to a field marked `data-autofocus`, else the
    // first interactive field in the body (not the header close button),
    // falling back to the dialog itself. The marker lets content skip leading
    // controls such as info icons, whose tooltip would otherwise open on load.
    const body = dialogRef.current?.querySelector('.modal-body');
    const firstField =
      body?.querySelector<HTMLElement>('[data-autofocus]') ??
      body?.querySelector<HTMLElement>(FOCUSABLE);
    (firstField ?? dialogRef.current)?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      // Another dialog on top (an attachment preview, a confirmation) owns the
      // keyboard: Escape closes that one, and Tab stays in it.
      const focused = document.activeElement as HTMLElement | null;
      if (
        focused &&
        rootRef.current &&
        !rootRef.current.contains(focused) &&
        focused.closest('.modal, [role="dialog"]')
      ) {
        return;
      }
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      // Trap Tab focus within the modal.
      const items = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement;
      if (event.shiftKey && (active === first || !dialogRef.current.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      openModalCount = Math.max(0, openModalCount - 1);
      if (openModalCount === 0) {
        document.body.style.overflow = savedBodyOverflow;
      }
      previouslyFocused.current?.focus?.();
    };
    // Setup runs once on mount; the keydown handler reads onCloseRef for the
    // latest onClose, so a changing onClose prop must not re-trigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={rootRef}
      className="modal d-block"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex, overflow: 'auto' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      // Close only when the backdrop itself is clicked, not content inside it.
      onClick={event => { if (event.target === event.currentTarget) onClose(); }}
    >
      <div
        className={[
          'modal-dialog modal-dialog-centered modal-dialog-scrollable',
          size ? `modal-${size}` : '',
          fullscreenBelow ? `modal-fullscreen-${fullscreenBelow}-down` : '',
          dialogClassName ?? '',
        ].filter(Boolean).join(' ')}
        ref={dialogRef}
        tabIndex={-1}
      >
        <div className="modal-content">
          <div className={['modal-header', headerClassName ?? ''].filter(Boolean).join(' ')}>
            {renderHeader ? renderHeader(titleId) : <h5 className="modal-title" id={titleId}>{title}</h5>}
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label={closeAriaLabel ?? t('common.close', 'Stäng')}
            ></button>
          </div>
          {subheader && <div className="modal-subheader">{subheader}</div>}
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  );
}
